import dayjs, { Dayjs } from "dayjs";
import {
  useState,
  useEffect,
  useRef,
  MouseEvent,
  useMemo,
  FunctionComponent,
  useContext
} from "react";
import {
  DayPicker,
  SelectRangeEventHandler,
  DateRange
} from "react-day-picker";
import { resources } from "../../utils/resources";
import "react-day-picker/dist/style.css";
import styles from "./RangePicker.module.scss";
import LayoutContext from "../../context/layout-context";
import Button from "../button/Button";

interface RangePickerProps {
  onChange: (dateRange: [Dayjs | null, Dayjs | null]) => void;
  value: [Dayjs | null, Dayjs | null];
  format?: string;
  dropdownOnTop?: boolean;
  responsive?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const RangePicker: FunctionComponent<RangePickerProps> = props => {
  const [showDropdown, setShowDropdown] = useState(false);

  const { notify } = useContext(LayoutContext);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const {
    value,
    onChange,
    format,
    dropdownOnTop,
    responsive,
    disabled,
    placeholder
  } = props;

  const handleClose = (e: globalThis.MouseEvent) => {
    const dropdown = dropdownRef.current;
    if (!dropdown || !dropdown.contains(e.target as Element)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    window.addEventListener("click", handleClose);
    return () => window.removeEventListener("click", handleClose);
  }, []);

  const handleDateSelect: SelectRangeEventHandler = (
    _dates,
    dateSelected,
    _activeModifiers,
    e
  ) => {
    if (value[0] && !value[1]) {
      const endDate = dateSelected ? dayjs(dateSelected) : null;
      if (endDate && value[0].isAfter(endDate)) {
        notify("error", "End date cannot come before start date");
        return;
      }
      onChange([value[0], endDate]);
      setShowDropdown(false);
    } else {
      onChange([dateSelected ? dayjs(dateSelected) : null, null]);
    }
    // onChange([from ? dayjs(from) : null, to ? dayjs(to) : null]);
    e.stopPropagation();
  };

  const handleSelectClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }
    const select = selectRef.current;
    if (select && select.contains(e.target as Element)) {
      setShowDropdown(!showDropdown);
    }
  };

  const initialMonth = useMemo(() => (value[0] || dayjs()).toDate(), [value]);
  const shownValue = useMemo(() => {
    return value[0] || value[1]
      ? value
          .map(date => date?.format(format || "YYYY-MM-DD") || "")
          .join(" - ")
      : null;
  }, [value, format]);

  const jsDateValues: DateRange = useMemo(() => {
    return {
      from: value[0]?.toDate() || undefined,
      to: value[1]?.toDate() || undefined
    };
  }, [value]);

  return (
    <div
      className={[
        styles["select-wrapper"],
        showDropdown ? styles.active : "",
        responsive && styles.responsive
      ].join(" ")}
      ref={dropdownRef}
      onClick={handleSelectClick}
      role="time"
    >
      <div className={styles["main-content"]} ref={selectRef}>
        {shownValue ? (
          <span className={styles["main-text"]}>{shownValue}</span>
        ) : (
          <span className={styles.placeholder}>{placeholder || "-"}</span>
        )}
        <img
          alt="calendar"
          className={styles.icon}
          src={resources.icons.CALENDAR}
        />
      </div>
      <div
        className={[
          styles.dropdown,
          showDropdown && styles["show-dropdown"],
          dropdownOnTop && styles["on-top"]
        ].join(" ")}
      >
        <DayPicker
          defaultMonth={initialMonth}
          selected={jsDateValues}
          onSelect={handleDateSelect}
          mode="range"
          modifiersClassNames={{
            selected: styles.selected,
            today: styles.today
          }}
          footer={
            (value[0] || value[1]) && (
              <Button
                type="accent"
                danger
                size="small"
                className="margin-top spaced"
                onClick={() => onChange([null, null])}
              >
                CLEAR
              </Button>
            )
          }
        />
      </div>
    </div>
  );
};

export default RangePicker;
